import { useState, useEffect, useRef, useCallback } from 'react';
import {
  streamAgent,
  stopAgent,
  getAgentStatus,
  getMessages,
  nonRunningAgentRuns,
} from '@/services/thread';
import { toast } from 'sonner';
import { safeJsonParse } from '@/components/thread/utils';
import {
  mapApiMessagesToMessages,
  mapApiMessageToMessage,
  processToolCalls,
} from '@/lib/thread';
import {
  EVT_UPDATE_CURRENT_PROJECT,
  EVT_UPDATE_PROJECT,
} from '@/constants/event';
import { useStores } from '@/store/hooks/useStore';
import { useI18n } from '@/hooks/use-i18n';

type IToolCallStreamInput = TOOL_CALL.StreamInput;
// Define the callbacks the hook consumer can provide
export interface AgentStreamCallbacks {
  onMessage: (message: MESSAGE.Message) => void; // Callback for complete messages
  onMessageInterrupt?: (messageId: string) => void; // Callback for message interrupts
  onStatusChange?: (status: string) => void; // Optional: Notify on internal status changes
  onError?: (error: string) => void; // Optional: Notify on errors
  onClose?: (finalStatus: string) => void; // Optional: Notify when streaming definitively ends
  onAssistantStart?: () => void; // Optional: Notify when assistant starts streaming
}
interface UseAgentStreamParams {
  callbacks: AgentStreamCallbacks;
  threadId: string;
}

// Define the structure returned by the hook
export interface UseAgentStreamResult {
  status: string;
  error: string | null;
  agentRunId: string | null; // Expose the currently managed agentRunId
  startStreaming: (runId: string) => void;
  /**
   * 停止当前会话：
   * - runId 可选：用于「刚创建 message，但 SSE 还未 startStreaming」的窗口期
   *   此时内部 agentRunId 可能尚未写入，但我们仍需要对这个 runId 调用 stop 接口。
   */
  stopStreaming: (runId?: string) => Promise<boolean>;
}

export function useAgentStream(
  params: UseAgentStreamParams,
): UseAgentStreamResult {
  const { callbacks, threadId } = params;
  const stores = useStores();
  const { streamingTextContent, setStreamingTextContent, setMessages } =
    stores.threadStore;
  const {
    setStreamingToolCalls,
    matchToolCategory,
    setNestingStreamingTextContent,
  } = stores.threadToolStore;
  const [agentRunId, setAgentRunId] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('idle');

  const [error, setError] = useState<string | null>(null);
  const { t } = useI18n('thread');

  const streamCleanupRef = useRef<(() => void) | null>(null);
  const isMountedRef = useRef<boolean>(true);
  const currentRunIdRef = useRef<string | null>(null); // Ref to track the run ID being processed
  const threadIdRef = useRef(threadId); // Ref to hold the current threadId
  // 累积嵌套 think tool_calls 的 argsStr，避免每个 chunk 独立解析导致流式卡顿
  const nestingToolCallsRef = useRef<IToolCallStreamInput[] | null>(null);

  // Update refs if threadId or setMessages changes
  useEffect(() => {
    threadIdRef.current = threadId;
  }, [threadId]);

  // Helper function to map backend status to frontend status string
  const mapAgentStatus = (backendStatus: string): string => {
    switch (backendStatus) {
      case 'completed':
        return 'completed';
      case 'stopped':
        return 'stopped';
      case 'failed':
        return 'failed';
      default:
        return 'error';
    }
  };

  // Internal function to update status and notify consumer
  const updateStatus = useCallback(
    (newStatus: string) => {
      if (isMountedRef.current) {
        setStatus(newStatus);
        callbacks.onStatusChange?.(newStatus);
        if (newStatus === 'error' && error) {
          callbacks.onError?.(error);
        }
        if (
          [
            'completed',
            'stopped',
            'failed',
            'error',
            'agent_not_running',
          ].includes(newStatus)
        ) {
          callbacks.onClose?.(newStatus);
        }
      }
    },
    [callbacks, error],
  ); // Include error dependency

  // Function to handle finalization of a stream (completion, stop, error)
  const finalizeStream = useCallback(
    (finalStatus: string, runId: string | null = agentRunId) => {
      if (!isMountedRef.current) {
        return;
      }

      const currentThreadId = threadIdRef.current; // Get current threadId from ref

      if (streamCleanupRef.current) {
        streamCleanupRef.current();
        streamCleanupRef.current = null;
      }

      // Reset streaming-specific state
      setStreamingTextContent('');
      setStreamingToolCalls(null);
      setNestingStreamingTextContent({});
      nestingToolCallsRef.current = null;

      // Update status and clear run ID
      updateStatus(finalStatus);
      setAgentRunId(null);
      currentRunIdRef.current = null;

      // --- Reliable Message Refetch on Finalization ---
      // Only refetch if the stream ended with a terminal status indicating the run is likely over
      const terminalStatuses = [
        'completed',
        'stopped',
        'failed',
        'error',
        'agent_not_running',
      ];
      if (currentThreadId && terminalStatuses.includes(finalStatus)) {
        getMessages(currentThreadId)
          .then((messagesData) => {
            if (isMountedRef.current && messagesData) {
              // todo 需要搞成构造树 set，但是现在展示上是正常的，先不处理
              const messages = mapApiMessagesToMessages(messagesData);
              setMessages(messages); // Use the ref'd setMessages
            } else if (!isMountedRef.current) {
              console.log(
                `[useAgentStream] Component unmounted before messages could be set after refetch for thread ${currentThreadId}.`,
              );
            }
          })
          .catch((err) => {
            console.error(
              `[useAgentStream] Error refetching messages for thread ${currentThreadId} after finalization:`,
              err,
            );
            // Optionally notify the user via toast or callback
            toast.error(`Failed to refresh messages: ${err.message}`);
          });
      } else {
        console.log(
          `[useAgentStream] Skipping message refetch for thread ${currentThreadId}. Final status: ${finalStatus}`,
        );
      }

      // If the run was stopped or completed, try to get final status to update nonRunning set (keep this)
      if (
        runId &&
        (finalStatus === 'completed' ||
          finalStatus === 'stopped' ||
          finalStatus === 'agent_not_running')
      ) {
        getAgentStatus(runId).catch((err) => {
          console.log(
            `[useAgentStream] Post-finalization status check for ${runId} failed (this might be expected if not found): ${err.message}`,
          );
        });
      }
    },
    [agentRunId, updateStatus],
  );

  // --- Stream Callback Handlers ---

  const handleStreamMessage = useCallback(
    (rawData: string) => {
      if (!isMountedRef.current) return;
      (window as any).lastStreamMessage = Date.now(); // Keep track of last message time

      let processedData = rawData;
      if (processedData.startsWith('data: ')) {
        processedData = processedData.substring(6).trim();
      }
      if (!processedData) return;

      // --- Early exit for non-JSON completion messages ---
      if (processedData === '[DONE]') {
        console.log(
          '[useAgentStream] Received final completion status message',
        );
        finalizeStream('completed', currentRunIdRef.current);
        return;
      }
      if (
        processedData.includes('Run data not available for streaming') ||
        processedData.includes('Stream ended with status: completed')
      ) {
        console.log(
          `[useAgentStream] Detected final completion message: "${processedData}", finalizing.`,
        );
        finalizeStream('completed', currentRunIdRef.current);
        return;
      }

      // --- Process JSON messages ---
      const apiMessage: MESSAGE.ApiMessage = safeJsonParse(processedData, null);
      if (!apiMessage) {
        console.warn(
          '[useAgentStream] Failed to parse streamed message:',
          processedData,
        );
        return;
      }
      const message = mapApiMessageToMessage(apiMessage);

      // Update status to streaming if we receive a valid message
      if (status !== 'streaming') updateStatus('streaming');

      const currentMessageMap = stores.threadStore.messageMap;
      const currentMessage = currentMessageMap[message.message_id];
      switch (message.type) {
        case 'assistant':
          const parsedContent = message.content;
          const parsedMetadata = message.metadata;
          let currentMessageMetadata: MESSAGE.Metadata = {};
          if (
            currentMessage &&
            (currentMessage as MESSAGE.AssistantMessage).metadata
          ) {
            currentMessageMetadata = (
              currentMessage as MESSAGE.AssistantMessage
            ).metadata;
          }
          // 如果当前消息是 complete，不予处理，直接返回
          if (currentMessageMetadata.stream_status === 'complete') {
            return;
          }
          if (
            parsedMetadata.stream_status === 'chunk' &&
            (parsedContent.content || parsedContent.tool_calls)
          ) {
            if (parsedContent.content) {
              // 判断是否为嵌套的流式输出
              const parentId =
                parsedContent.parent_ids?.[parsedContent.parent_ids.length - 1];
              const lastForgeMessageMap =
                stores.threadStore.lastForgeMessageMap;
              // 消息带有父节点，说明为嵌套的流式输出
              if (parentId) {
                setNestingStreamingTextContent((prev) => ({
                  ...prev,
                  content: (prev?.content || '') + parsedContent.content,
                  parent_ids: parsedContent.parent_ids,
                }));
              } else if (
                lastForgeMessageMap &&
                lastForgeMessageMap['browser']
              ) {
                // 前端构造的嵌套结构里的流式输出
                setNestingStreamingTextContent((prev) => ({
                  ...prev,
                  content: (prev?.content || '') + parsedContent.content,
                  parent_ids: [
                    lastForgeMessageMap['browser'],
                    lastForgeMessageMap['browser_step'],
                  ],
                }));
              } else {
                // 最外层的流式输出
                setStreamingTextContent((prev) => prev + parsedContent.content);
              }
            }
            if (parsedContent.tool_calls) {
              const parentId =
                parsedContent.parent_ids?.[parsedContent.parent_ids.length - 1];
              // 嵌套消息的 tool_calls 不更新外层 streamingToolCalls，只同步到嵌套流式内容
              if (parentId) {
                nestingToolCallsRef.current = processToolCalls(
                  nestingToolCallsRef.current,
                  parsedContent.tool_calls as TOOL_CALL.StreamInput[],
                  matchToolCategory,
                );
                const thinkTC = nestingToolCallsRef.current?.find(
                  (tc) => (tc.name as string) === 'think',
                );
                if (thinkTC?.args?.thought) {
                  setNestingStreamingTextContent({
                    content: thinkTC.args.thought,
                    parent_ids: parsedContent.parent_ids,
                  });
                }
              } else {
                const lastForgeMessageMap =
                  stores.threadStore.lastForgeMessageMap;
                if (lastForgeMessageMap?.['browser']) {
                  // browser 上下文中，tool_calls 不更新外层 streamingToolCalls，
                  // 只将 think 的流式思考内容同步到 nestingStreamingTextContent
                  nestingToolCallsRef.current = processToolCalls(
                    nestingToolCallsRef.current,
                    parsedContent.tool_calls as TOOL_CALL.StreamInput[],
                    matchToolCategory,
                  );
                  const thinkTC = nestingToolCallsRef.current?.find(
                    (tc) => (tc.name as string) === 'think',
                  );
                  if (thinkTC?.args?.thought) {
                    setNestingStreamingTextContent({
                      content: thinkTC.args.thought,
                      parent_ids: [
                        lastForgeMessageMap['browser'],
                        lastForgeMessageMap['browser_step'],
                      ],
                    });
                  }
                } else {
                  setStreamingToolCalls((prev) => {
                    return processToolCalls(
                      prev,
                      parsedContent.tool_calls as TOOL_CALL.StreamInput[],
                      matchToolCategory,
                    );
                  });
                }
              }
            }
          } else if (parsedMetadata.stream_status === 'complete') {
            setStreamingTextContent('');
            setStreamingToolCalls(null);
            nestingToolCallsRef.current = null;
            const lastForgeMap = stores.threadStore.lastForgeMessageMap;
            if (!lastForgeMap?.['browser']) {
              setNestingStreamingTextContent({});
            } else {
              // browser 上下文中，think 消息完成时确保 nestingStreamingTextContent 包含 think 内容
              // 流式 chunk 阶段依赖 JSON 增量解析，可能未成功同步 think 内容，这里用完整消息兜底
              const thinkToolCall = parsedContent.tool_calls?.find(
                (tc: any) => (tc.name as string) === 'think',
              );
              const thought =
                typeof thinkToolCall?.args === 'string'
                  ? undefined
                  : thinkToolCall?.args?.thought;
              if (thought) {
                setNestingStreamingTextContent({
                  content: thought,
                  parent_ids: [
                    lastForgeMap['browser'],
                    lastForgeMap['browser_step'],
                  ],
                });
              }
            }
            if (message.message_id) {
              callbacks.onMessage(message);
            }
          } else if (!parsedMetadata.stream_status) {
            // Handle non-chunked assistant messages if needed
            callbacks.onAssistantStart?.();
            if (message.message_id) {
              callbacks.onMessage(message);
            }
          }
          break;
        case 'tool':
          setStreamingToolCalls(null); // Clear any streaming tool call
          // browser 上下文中不清空 nestingStreamingTextContent，保留累积的流式内容
          if (!stores.threadStore.lastForgeMessageMap?.['browser']) {
            setNestingStreamingTextContent({});
          } else if (
            (message as MESSAGE.ToolMessage).content?.name === 'think'
          ) {
            // browser 上下文中的 think tool 消息，添加 parent_ids 防止外露到顶层
            const lastForgeMap = stores.threadStore.lastForgeMessageMap;
            message.metadata = message.metadata || {};
            message.metadata.parent_ids = message.metadata.parent_ids || [];
            message.metadata.parent_ids.push(
              lastForgeMap['browser'],
              lastForgeMap['browser_step'],
            );
          }
          if (message.message_id) {
            callbacks.onMessage(message);
          }
          break;
        case 'status': {
          // 2025-12-24 约定：type=status 且顶层 status 有值（非空） 我们认为该任务已经结束，需要关闭 stream
          const endStatus = message.status;

          /**
           * 会话结束状态枚举
           * endStatus 表示后端返回的 status 值会走业务逻辑，finalStatus 表示最终的结束状态，
           * 不会跟随业务增加而增加
           */
          const END_STATUS_ENUM = [
            { endStatus: 'no_quota', finalStatus: 'error' },
            { endStatus: 'device_not_connected', finalStatus: 'error' },
            { endStatus: 'platform_not_login', finalStatus: 'error' },

            { endStatus: 'full_completed', finalStatus: 'completed' },
            { endStatus: 'async_tag_task', finalStatus: 'completed' },
            { endStatus: 'async_task', finalStatus: 'completed' },
            { endStatus: 'ask_human', finalStatus: 'completed' },

            { endStatus: 'stopped', finalStatus: 'stopped' },
            { endStatus: 'failed', finalStatus: 'failed' },
          ] as const;

          // TODO: 断流判断我们约定的是 type: status & status 有值 即断开连接；
          // 那么这里需要使用用白名单（END_STATUS_ENUM）避免误判未知 status 吗？
          const endStatusConfig = END_STATUS_ENUM.find(
            (x) => x.endStatus === endStatus,
          );

          if (endStatus && !endStatusConfig) {
            console.error('[useAgentStream] 收到未处理的状态类型:', endStatus);
          }

          if (endStatus && endStatusConfig) {
            callbacks.onMessage(message);

            let finalStatus: string =
              endStatusConfig.finalStatus ?? 'completed';

            // 处理一下特殊的Error状态
            if ((message as any)?.content?.status_type === 'error') {
              console.error(
                '[handleStreamMessage] 判定任务结束，finalStatus为：error，错误信息为：',
                (message as any)?.content?.message,
              );
              setError(
                (message as any)?.content?.message || 'Agent run failed',
              );
              finalStatus = 'error';
            }

            console.log(
              '[handleStreamMessage] 判定任务结束，finalStatus为：',
              finalStatus,
            );

            finalizeStream(finalStatus, currentRunIdRef.current);

            // 注意：当我们收到了 type: status & status 有值 且 status 在白名单（END_STATUS_ENUM）中，
            // 我们认为是任务结束，需要关闭 stream。
            // 按道理下面的switch (message.content.status_type) 应该不会执行到。
            break;
          }

          switch (message.content.status_type) {
            case 'worker':
              if (message.message_id) {
                callbacks.onMessage(message);
              }
              break;
            case 'thread_run_end':
              console.log(
                '[useAgentStream] Received thread run end status, finalizing.',
              );
              break;
            case 'finish':
              // Optional: Handle finish reasons like 'xml_tool_limit_reached'
              console.log(
                '[useAgentStream] Received finish status:',
                message.content.finish_reason,
              );
              // Don't finalize here, wait for thread_run_end or completion message
              break;
            case 'error':
              console.error(
                '[useAgentStream] Received error status message:',
                message.content.message,
              );
              setError(message.content.message || 'Agent run failed');
              finalizeStream('error', currentRunIdRef.current);
              break;
            case 'thread_interrupted':
              toast.warning(t('sessionInterrupted'));
              // 清空当前流式消息
              setStreamingTextContent('');
              setStreamingToolCalls(null);
              setNestingStreamingTextContent({});
              // 清空被中断的 assistant 消息
              if (message.content.message_id) {
                callbacks.onMessageInterrupt?.(message.content.message_id);
              }
              break;
            default:
              // console.debug('[useAgentStream] Received unhandled status type:', parsedContent.status_type);
              break;
          }
          break;
        }
        case 'user':
          // Handle other message types if necessary, e.g., if backend sends historical context
          if (message.message_id) {
            callbacks.onMessage(message);
          }
          break;
        case 'project_info':
          // Handle project info message
          if (typeof window !== 'undefined') {
            const projectId = message.project_id;
            // 更新会话列表中的对应数据
            window.dispatchEvent(
              new CustomEvent(EVT_UPDATE_PROJECT, {
                detail: {
                  projectId,
                  updatedData: {
                    name: message.name,
                    description: message.description,
                  },
                },
              }),
            );
            // 更新当前会话的标题数据
            window.dispatchEvent(
              new CustomEvent(EVT_UPDATE_CURRENT_PROJECT, {
                detail: {
                  projectId,
                  updatedData: {
                    id: projectId,
                    name: message.name,
                  },
                },
              }),
            );
          }
          break;
        default:
          console.warn(
            '[useAgentStream] Unhandled message type:',
            (message as any).type,
          );
      }
    },
    [threadId, setMessages, status, callbacks, finalizeStream, updateStatus],
  );

  const handleStreamError = useCallback(
    (err: Error | string | Event) => {
      if (!isMountedRef.current) return;

      // Extract error message
      let errorMessage = 'Unknown streaming error';
      if (typeof err === 'string') {
        errorMessage = err;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      } else if (err instanceof Event && err.type === 'error') {
        // Standard EventSource errors don't have much detail, might need status check
        errorMessage = 'Stream connection error';
      }

      console.error('[useAgentStream] Streaming error:', errorMessage, err);
      setError(errorMessage);

      const runId = currentRunIdRef.current;
      if (!runId) {
        console.warn(
          '[useAgentStream] Stream error occurred but no agentRunId is active.',
        );
        finalizeStream('error'); // Finalize with generic error if no runId
        return;
      }

      // 检查该 agent run 是否已知不在运行状态，避免不必要的状态查询
      if (nonRunningAgentRuns.has(runId)) {
        console.log(
          `[useAgentStream] Stream error for ${runId}, which is already known to be non-running. Finalizing as agent_not_running.`,
        );
        finalizeStream('agent_not_running', runId);
        return;
      }

      // Check agent status immediately after an error
      getAgentStatus(runId)
        .then((agentStatus) => {
          if (!isMountedRef.current) return; // Check mount status again after async call

          if (agentStatus.status === 'running') {
            console.warn(
              `[useAgentStream] Stream error for ${runId}, but agent is still running. Finalizing with error.`,
            );
            finalizeStream('error', runId); // Stream failed, even if agent might still be running backend-side
            toast.warning('Stream interrupted. Agent might still be running.');
          } else {
            // Map backend terminal status to hook terminal status
            const finalStatus = mapAgentStatus(agentStatus.status);
            console.log(
              `[useAgentStream] Stream error for ${runId}, agent status is ${agentStatus.status}. Finalizing stream as ${finalStatus}.`,
            );
            finalizeStream(finalStatus, runId);
          }
        })
        .catch((statusError) => {
          if (!isMountedRef.current) return;

          const statusErrorMessage =
            statusError instanceof Error
              ? statusError.message
              : String(statusError);
          console.error(
            `[useAgentStream] Error checking agent status for ${runId} after stream error: ${statusErrorMessage}`,
          );

          const isNotFoundError =
            statusErrorMessage.includes('not found') ||
            statusErrorMessage.includes('404') ||
            statusErrorMessage.includes('does not exist');

          if (isNotFoundError) {
            console.log(
              `[useAgentStream] Agent run ${runId} not found after stream error. Finalizing.`,
            );
            // Revert to agent_not_running for this specific case
            finalizeStream('agent_not_running', runId);
          } else {
            // For other status check errors, finalize with the original stream error
            finalizeStream('error', runId);
          }
        });
    },
    [finalizeStream],
  );

  const handleStreamClose = useCallback(() => {
    if (!isMountedRef.current) return;
    console.log('[useAgentStream] Stream connection closed by server.');

    const runId = currentRunIdRef.current;
    if (!runId) {
      // 没有 runId 基本意味着我们已经 finalize/cleanup 过了（例如在 status 分支里主动 finalize）
      // 这里直接返回，避免二次 finalize 覆盖状态。
      return;
    }

    // 检查该 agent run 是否已知不在运行状态，避免不必要的状态查询
    if (nonRunningAgentRuns.has(runId)) {
      console.log(
        `[useAgentStream] Stream closed for ${runId}, which is already known to be non-running. Finalizing as agent_not_running.`,
      );
      finalizeStream('agent_not_running', runId);
      return;
    }

    // Immediately check the agent status when the stream closes unexpectedly
    // This covers cases where the agent finished but the final message wasn't received,
    // or if the agent errored out on the backend.
    getAgentStatus(runId)
      .then((agentStatus) => {
        if (!isMountedRef.current) return; // Check mount status again

        console.log(
          `[useAgentStream] Agent status after stream close for ${runId}: ${agentStatus.status}`,
        );
        if (agentStatus.status === 'running') {
          console.warn(
            `[useAgentStream] Stream closed for ${runId}, but agent is still running. Finalizing with error.`,
          );
          setError('Stream closed unexpectedly while agent was running.');
          finalizeStream('error', runId); // Finalize as error for now
          toast.warning('Stream disconnected. Agent might still be running.');
        } else {
          // Map backend terminal status to hook terminal status
          const finalStatus = mapAgentStatus(agentStatus.status);
          console.log(
            `[useAgentStream] Stream closed for ${runId}, agent status is ${agentStatus.status}. Finalizing stream as ${finalStatus}.`,
          );
          finalizeStream(finalStatus, runId);
        }
      })
      .catch((err) => {
        if (!isMountedRef.current) return;

        const errorMessage = err instanceof Error ? err.message : String(err);
        console.error(
          `[useAgentStream] Error checking agent status for ${runId} after stream close: ${errorMessage}`,
        );

        const isNotFoundError =
          errorMessage.includes('not found') ||
          errorMessage.includes('404') ||
          errorMessage.includes('does not exist');

        if (isNotFoundError) {
          console.log(
            `[useAgentStream] Agent run ${runId} not found after stream close. Finalizing.`,
          );
          // Revert to agent_not_running for this specific case
          finalizeStream('agent_not_running', runId);
        } else {
          // For other errors checking status, finalize with generic error
          finalizeStream('error', runId);
        }
      });
  }, [status, finalizeStream]); // Include status

  // --- Effect to manage the stream lifecycle ---
  useEffect(() => {
    isMountedRef.current = true;

    // Cleanup function for when the component unmounts or agentRunId changes
    return () => {
      isMountedRef.current = false;
      console.log(
        '[useAgentStream] Unmounting or agentRunId changing. Cleaning up stream.',
      );
      if (streamCleanupRef.current) {
        streamCleanupRef.current();
        streamCleanupRef.current = null;
      }
      // Reset state on unmount if needed, though finalizeStream should handle most cases
      setStatus('idle');
      setStreamingTextContent('');
      setStreamingToolCalls(null);
      setNestingStreamingTextContent({});
      nestingToolCallsRef.current = null;
      setError(null);
      setAgentRunId(null);
      currentRunIdRef.current = null;
    };
  }, []); // Empty dependency array for mount/unmount effect

  // --- Public Functions ---

  const startStreaming = useCallback(
    async (runId: string) => {
      if (!isMountedRef.current) return;
      console.log(
        `[useAgentStream] Received request to start streaming for ${runId}`,
      );

      // Clean up any previous stream
      if (streamCleanupRef.current) {
        console.log(
          '[useAgentStream] Cleaning up existing stream before starting new one.',
        );
        streamCleanupRef.current();
        streamCleanupRef.current = null;
      }

      // Reset state before starting
      setStreamingTextContent('');
      setStreamingToolCalls(null);
      setNestingStreamingTextContent({});
      nestingToolCallsRef.current = null;
      setError(null);
      updateStatus('connecting');
      setAgentRunId(runId);
      currentRunIdRef.current = runId; // Set the ref immediately

      try {
        // *** Crucial check: Verify agent is running BEFORE connecting ***
        const agentStatus = await getAgentStatus(runId);
        if (!isMountedRef.current) return; // Check mount status after async call

        if (agentStatus.status !== 'running') {
          console.warn(
            `[useAgentStream] Agent run ${runId} is not in running state (status: ${agentStatus.status}). Cannot start stream.`,
          );
          setError(`Agent run is not running (status: ${agentStatus.status})`);
          finalizeStream(
            mapAgentStatus(agentStatus.status) || 'agent_not_running',
            runId,
          );
          return;
        }

        // Agent is running, proceed to create the stream
        console.log(
          `[useAgentStream] Agent run ${runId} confirmed running. Setting up EventSource.`,
        );
        const cleanup = streamAgent(runId, {
          onMessage: handleStreamMessage,
          onError: handleStreamError,
          onClose: handleStreamClose,
        });
        streamCleanupRef.current = cleanup;
        // Status will be updated to 'streaming' by the first message received in handleStreamMessage
      } catch (err) {
        if (!isMountedRef.current) return; // Check mount status after async call

        const errorMessage = err instanceof Error ? err.message : String(err);
        console.error(
          `[useAgentStream] Error initiating stream for ${runId}: ${errorMessage}`,
        );
        setError(errorMessage);

        const isNotFoundError =
          errorMessage.includes('not found') ||
          errorMessage.includes('404') ||
          errorMessage.includes('does not exist');

        finalizeStream(isNotFoundError ? 'agent_not_running' : 'error', runId);
      }
    },
    [
      updateStatus,
      finalizeStream,
      handleStreamMessage,
      handleStreamError,
      handleStreamClose,
    ],
  ); // Add dependencies

  const stopStreaming = useCallback(
    async (runId?: string) => {
      if (!isMountedRef.current) {
        return false;
      }

      // 优先使用显式传入的 runId，其次用当前 hook 的 agentRunId
      const runIdToStop = runId || agentRunId;
      if (!runIdToStop) {
        // 这里不能当作成功，否则会触发二次发送链路继续推进
        console.warn(
          '[useAgentStream] stopStreaming: 缺少 agentRunId，跳过停止',
        );
        return false;
      }

      try {
        await stopAgent(runIdToStop);
        toast.success(t('sessionAborted'));
        // update status and clean up stream after stop resolve
        finalizeStream('stopped', runIdToStop);
        return true;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        console.error(
          `[useAgentStream] 停止 ${runIdToStop} 时出现错误: ${errorMessage}`,
        );
        return false;
      }
    },
    [agentRunId, finalizeStream],
  ); // Add dependencies

  return {
    status,
    error,
    agentRunId,
    startStreaming,
    stopStreaming,
  };
}
